import { Card, CardContent } from '@/components/ui/card';
import { Mail } from 'lucide-react';
import { useLanguage } from '@/components/LanguageContext';

/**
 * Renderiza un bloque de contenido de un documento legal.
 * Tipos soportados:
 *   - { type: 'p', text }            → párrafo
 *   - { type: 'h', text }            → subtítulo de sección (p. ej. "7.1 …")
 *   - { type: 'ul', items: [] }      → lista con viñetas
 *   - { type: 'table', headers, rows } → tabla (p. ej. listado de cookies)
 */
function Block({ block }) {
  switch (block.type) {
    case 'ul':
      return (
        <ul className="list-disc pl-5 space-y-2">
          {(block.items || []).map((it, k) => (
            <li key={k}>{it}</li>
          ))}
        </ul>
      );
    case 'h':
      return <h3 className="text-lg font-bold text-slate-800 pt-2">{block.text}</h3>;
    case 'table':
      return (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm text-left border-collapse">
            {Array.isArray(block.headers) && block.headers.length > 0 ? (
              <thead>
                <tr className="bg-slate-50">
                  {block.headers.map((h, k) => (
                    <th key={k} className="px-4 py-3 font-bold text-slate-900 border-b border-slate-200 align-top">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {(block.rows || []).map((row, r) => (
                <tr key={r} className="even:bg-slate-50/50">
                  {row.map((cell, c) => (
                    <td key={c} className="px-4 py-3 border-b border-slate-100 align-top text-slate-600">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'p':
    default:
      return <p>{block.text}</p>;
  }
}

/**
 * Renderiza un documento legal (Términos, Privacidad, DPA, Aviso Legal, Cookies)
 * a partir de una única clave de traducción estructurada, reproduciendo el estilo
 * visual de la página de Términos.
 *
 * La clave `docKey` debe resolver (con returnObjects) a un objeto con la forma:
 * {
 *   title: string,
 *   subtitle?: string,
 *   platform?: string,
 *   last_update?: string,
 *   contact_email?: string,
 *   intro?: Block[],
 *   sections: Array<{ n?: string, title: string, blocks: Block[] }>
 * }
 *
 * @param {{ docKey: string, icon: React.ComponentType<{ className?: string }> }} props
 */
export default function LegalDocument({ docKey, icon: Icon }) {
  const { t } = useLanguage();
  const doc = t(docKey, { returnObjects: true });

  // Si la clave no resuelve a un objeto (falta de traducción), no renderizamos basura.
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.sections)) {
    return null;
  }

  // El correo de la tarjeta de contacto del final es siempre el de soporte,
  // independientemente del email que cada documento cite en su cuerpo legal.
  const contactEmail = 'soporte@tapstar.es';
  const intro = Array.isArray(doc.intro) ? doc.intro : [];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-10 py-12">
      <header className="text-center space-y-4">
        <div className="inline-flex items-center justify-center p-3 bg-blue-50 rounded-2xl mb-2">
          {Icon ? <Icon className="h-8 w-8 text-slate-900" /> : null}
        </div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">{doc.title}</h1>
        {doc.subtitle ? (
          <p className="text-lg font-semibold text-slate-500">{doc.subtitle}</p>
        ) : null}
        <div className="flex flex-col items-center gap-1 text-slate-500">
          {doc.platform ? <p className="text-sm">{doc.platform}</p> : null}
          <p className="font-medium">{t('titular_label')}: Speed Data Technologies, S.L.</p>
          {doc.last_update ? (
            <p className="text-sm">{t('last_update_label')}: {doc.last_update}</p>
          ) : null}
        </div>
      </header>

      <Card className="border-none shadow-2xl rounded-[2.5rem] overflow-hidden bg-white">
        <CardContent className="p-8 md:p-14 prose prose-slate max-w-none">
          {intro.length > 0 ? (
            <div className="space-y-4 text-slate-600 leading-relaxed mb-10">
              {intro.map((block, j) => (
                <Block key={j} block={block} />
              ))}
            </div>
          ) : null}

          <section className="space-y-10">
            {doc.sections.map((sec, i) => (
              <div key={i} className="space-y-4 text-slate-600 leading-relaxed">
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                  {sec.n ? (
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-sm font-bold shrink-0">
                      {sec.n}
                    </span>
                  ) : null}
                  {sec.title}
                </h2>
                {(sec.blocks || []).map((block, j) => (
                  <Block key={j} block={block} />
                ))}
              </div>
            ))}
          </section>

          <div className="mt-16 p-8 bg-slate-50 rounded-3xl border border-slate-100 text-center space-y-4">
            <h3 className="text-lg font-bold text-slate-900">{t('have_questions')}</h3>
            <p className="text-slate-600 text-sm">{t('legal_questions_desc')}</p>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-2 text-slate-900 font-bold hover:underline"
            >
              <Mail className="h-4 w-4" />
              {contactEmail}
            </a>
            <p className="text-slate-400 text-xs mt-4">Speed Data Technologies, S.L.</p>
          </div>
        </CardContent>
      </Card>

      <footer className="text-center text-slate-400 text-sm flex flex-col items-center gap-2">
        <p>© 2026 Tapstar. {t('all_rights_reserved_legal')}</p>
      </footer>
    </div>
  );
}
